import { type Common } from '../common.js';
import {
  type FindAllOptions,
  type FlowTrigger,
  Paths,
  type TriggerKind,
  type TriggerTypeMap,
} from '../models/flow/flowTrigger.js';
import { isDefined } from '../utils.js';

export class API {
  common: Common;
  baseEndpoint = '/flow/triggers';
  constructor(common: Common) {
    this.common = common;
  }

  getUrl({ kind, id }: { kind: TriggerKind; id?: string }): string {
    const triggerRoute = Paths[kind];
    if (!isDefined(triggerRoute)) {
      throw new Error(
        `No flow-service route is defined for trigger kind '${kind}'. ` +
          'This kind is owned by another service (e.g. resource-gateway for RESOURCE_EVENT) ' +
          'and must be accessed via the corresponding client.'
      );
    }
    const path = id ? `${triggerRoute}/${id}` : triggerRoute;
    return this.common.constructPath(`${this.baseEndpoint}/${path}`);
  }

  /**
   * Returns the triggers list, discarding the aggregator's `partial` and
   * `missingSources` envelope fields. Callers that need to surface
   * partial-failure state (e.g. an admin list page) should use
   * {@link findAllWithMeta} instead.
   */
  async findAll<T extends FlowTrigger>(queryParams?: FindAllOptions): Promise<T[]> {
    const { triggers } = await this.findAllWithMeta(queryParams);
    return triggers as T[];
  }

  async findAllWithMeta(
    queryParams?: FindAllOptions
  ): Promise<{ triggers: FlowTrigger[]; partial: boolean; missingSources: string[] }> {
    const url = this.common.constructPath(this.baseEndpoint, queryParams);
    const response = await this.common.getJSON(url);
    return {
      triggers: response.triggers,
      partial: response.partial ?? false,
      missingSources: response.missingSources ?? [],
    };
  }

  async findOneById<T extends keyof TriggerTypeMap>(
    id: string,
    kind: T
  ): Promise<TriggerTypeMap[T]> {
    const url = this.getUrl({ id, kind });
    const trigger = await this.common.getJSON(url);
    return trigger.trigger;
  }

  async update<T extends keyof TriggerTypeMap>(
    id: string,
    kind: T,
    updateValues: Omit<
      TriggerTypeMap[T],
      'id' | 'created' | 'updated' | 'flowId' | 'kind' | 'organizationId'
    >
  ): Promise<TriggerTypeMap[T]> {
    const url = this.getUrl({ id, kind });
    const trigger = await this.common.putJSON(url, updateValues);
    return trigger.trigger;
  }

  async create<T extends keyof TriggerTypeMap>(
    createValues: Omit<TriggerTypeMap[T], 'id' | 'created' | 'updated'>
  ): Promise<TriggerTypeMap[T]> {
    const { kind, ...newTrigger } = createValues;
    const url = this.getUrl({ kind });
    const trigger = await this.common.postJSON(url, newTrigger);
    return trigger.trigger;
  }

  async delete(id: string, kind: TriggerKind): Promise<boolean> {
    const url = this.getUrl({ id, kind });
    const result = await this.common.deleteJSON(url);
    return result.success;
  }
}
